import { Injectable, inject } from '@angular/core';
import { CatalogPlan } from '@shared/models/plan-catalog.model';
import { Observable, map } from 'rxjs';
import {
  OrganizationSubscription, PaymentMethod, PlanChangePreview, StartPaymentMethodUpdateResult,
} from '../../domain/models/organization-subscription.model';
import { IOrganizationSubscriptionRepository } from '../../domain/repositories/organization-subscription.repository';
import { OrganizationSubscriptionRemoteDatasource } from '../datasources/remote/organization-subscription.remote-datasource';
import { OrganizationSubscriptionMapper } from '../mappers/organization-subscription.mapper';

@Injectable()
export class OrganizationSubscriptionRepositoryImpl implements IOrganizationSubscriptionRepository {
  private readonly datasource = inject(OrganizationSubscriptionRemoteDatasource);
  private readonly mapper = inject(OrganizationSubscriptionMapper);

  public get(organizationId: number): Observable<OrganizationSubscription> {
    return this.datasource.get(organizationId).pipe(
      map((dto) => this.mapper.toOrganizationSubscription(dto)),
    );
  }

  public setAutoCharge(organizationId: number, enabled: boolean): Observable<OrganizationSubscription> {
    return this.datasource.setAutoCharge(organizationId, enabled).pipe(
      map((dto) => this.mapper.toOrganizationSubscription(dto)),
    );
  }

  public getPlans(): Observable<readonly CatalogPlan[]> {
    return this.datasource.getPlans().pipe(map((dtos) => this.mapper.toCatalogPlans(dtos)));
  }

  public getPlanChangePreview(
    organizationId: number,
    planId: string,
    billingPeriod: string,
  ): Observable<PlanChangePreview> {
    return this.datasource.getPlanChangePreview(organizationId, planId, billingPeriod).pipe(
      map((dto) => this.mapper.toPlanChangePreview(dto)),
    );
  }

  public changePlan(
    organizationId: number,
    planId: string,
    billingPeriod: string,
  ): Observable<OrganizationSubscription> {
    return this.datasource.changePlan(organizationId, planId, billingPeriod).pipe(
      map((dto) => this.mapper.toOrganizationSubscription(dto)),
    );
  }

  public getPaymentMethod(organizationId: number): Observable<PaymentMethod | null> {
    return this.datasource.getPaymentMethod(organizationId).pipe(
      map((dto) => (dto ? this.mapper.toPaymentMethod(dto) : null)),
    );
  }

  public startPaymentMethodUpdate(organizationId: number): Observable<StartPaymentMethodUpdateResult> {
    return this.datasource.startPaymentMethodUpdate(organizationId).pipe(
      map((dto) => this.mapper.toStartPaymentMethodUpdateResult(dto)),
    );
  }

  public confirmPaymentMethodUpdate(
    organizationId: number,
    setupIntentId: string,
  ): Observable<PaymentMethod> {
    return this.datasource.confirmPaymentMethodUpdate(organizationId, setupIntentId).pipe(
      map((dto) => this.mapper.toPaymentMethod(dto)),
    );
  }

  public removePaymentMethod(organizationId: number): Observable<void> {
    return this.datasource.removePaymentMethod(organizationId);
  }
}
